import type { DemoRequestHandler } from '../services/BackendApiClient';
import type {
  Attachment, FinancialYearDetail, FinancialYearSummary, Meeting, Member, Notifications, PaymentPeriod, Post,
} from '../types/models';
import { createDemoStore, DEMO_USER_ID, demoFileUrl, type DemoFinancialYear, type DemoStore } from './demoSeed';

export const DEMO_NOTICE_EVENT = 'ra:demo-notice';
export const NO_MESSAGES_NOTICE = 'I testföreningen skickas inga SMS eller e-post.';

type Params = string[];
type Body = any; // eslint-disable-line @typescript-eslint/no-explicit-any
type Handler = (params: Params, body: Body) => unknown;

const notAvailable = () => { throw new Error('Inte tillgängligt i testföreningen.'); };
const now = () => new Date().toISOString();
let nextId = 1;
const newId = (prefix: string) => `${prefix}-new-${nextId++}`;

function uploadedFile(body: Body): { name: string; size: number; url: string } {
  const file = body instanceof FormData ? body.get('file') : null;
  if (file instanceof File) return { name: file.name, size: file.size, url: URL.createObjectURL(file) };
  return { name: 'fil', size: 0, url: demoFileUrl('fil') };
}

function notify(message: string) {
  window.dispatchEvent(new CustomEvent(DEMO_NOTICE_EVENT, { detail: message }));
}

function find<T extends { id: string }>(items: T[], id: string): T {
  const item = items.find(i => i.id === id);
  if (!item) throw new Error('Hittades inte.');
  return item;
}

export class DemoBackend implements DemoRequestHandler {
  private readonly store: DemoStore;
  private readonly routes: [string, RegExp, Handler][];

  constructor(viewer?: { fullName: string; email: string }) {
    this.store = createDemoStore(viewer);
    this.routes = this.buildRoutes();
  }

  async handle(method: string, url: string, body?: unknown): Promise<unknown> {
    const path = url.split('?')[0];
    for (const [routeMethod, pattern, handler] of this.routes) {
      const match = routeMethod === method ? pattern.exec(path) : null;
      if (match) return copy(handler(match.slice(1), body));
    }
    return notAvailable();
  }

  async download(url: string): Promise<Blob> {
    const name = decodeURIComponent(url.split('/').slice(-2).join(' '));
    return new Blob([`Demo – ${name}\n\nDetta är en exempelfil från Test förening.`], { type: 'text/plain' });
  }

  private me(): Member {
    return find(this.store.members, 'm-me');
  }

  private author() {
    const { id, email, fullName } = this.store.profile;
    return { id, email, fullName };
  }

  private financialYearSummary(year: DemoFinancialYear): FinancialYearSummary {
    const payments = this.store.paymentPeriods.filter(p => p.financialYearId === year.id).flatMap(p => p.memberPayments);
    const totalIncome = payments.filter(p => p.isPaid).reduce((sum, p) => sum + p.amount, 0);
    const totalReceivables = payments.filter(p => !p.isPaid).reduce((sum, p) => sum + p.amount, 0);
    const totalExpenses = year.supplierInvoices.filter(i => i.isPaid).reduce((sum, i) => sum + i.amount, 0)
      + year.otherCosts.reduce((sum, c) => sum + c.amount, 0);
    const { supplierInvoices, otherCosts, ...summary } = year;
    return { ...summary, totalIncome, totalExpenses, totalReceivables, balance: year.openingBalance + totalIncome - totalExpenses };
  }

  private memberPayments(period: Pick<PaymentPeriod, 'amount'>, existing: PaymentPeriod['memberPayments'] = []) {
    return this.store.members.map(m => {
      const previous = existing.find(p => p.memberId === m.id);
      return {
        id: previous?.id ?? newId('mp'), memberId: m.id, memberName: m.fullName,
        amount: period.amount * (m.sharePercentage / 100),
        isPaid: previous?.isPaid ?? false, paidAt: previous?.paidAt ?? null,
        invoiceNumber: previous?.invoiceNumber ?? null, invoiceDownloadUrl: previous?.invoiceDownloadUrl ?? null,
        reminderSentAt: previous?.reminderSentAt ?? null,
      };
    });
  }

  private buildRoutes(): [string, RegExp, Handler][] {
    const s = this.store;
    const org = '^/organizations/[^/]+';
    const id = '([^/]+)';
    const r = (method: string, path: string, handler: Handler): [string, RegExp, Handler] =>
      [method, new RegExp(`${path}$`), handler];
    const attachment = (body: Body): Attachment => {
      const file = uploadedFile(body);
      return { id: newId('a'), fileName: file.name, fileSize: file.size, downloadUrl: file.url };
    };
    const syncMeetingPost = (meeting: Meeting) => {
      const post = s.posts.find(p => p.meeting?.id === meeting.id);
      if (post) {
        const { id: meetingId, title, description, meetingDate, location } = meeting;
        Object.assign(post, { title, content: description, meeting: { id: meetingId, title, description, meetingDate, location } });
      }
    };
    const year = (yearId: string) => find(s.financialYears, yearId);

    return [
      r('GET', '^/users/me', () => s.profile),
      r('PUT', '^/users/me', (_, b) => Object.assign(s.profile, b)),

      r('GET', '^/organizations', () => [{ ...s.org, memberCount: s.members.length }]),
      r('POST', '^/organizations', notAvailable),
      r('PUT', org, (_, b) => Object.assign(s.org, b)),
      r('PUT', `${org}/billing-info`, (_, b) => Object.assign(s.org, b)),

      r('GET', `${org}/members`, () => s.members),
      r('POST', `${org}/members`, (_, b) => {
        const member: Member = { ...b, id: newId('m'), userId: newId('u'), createdAt: now(), isPending: true };
        s.members.push(member);
        notify(NO_MESSAGES_NOTICE);
        return member;
      }),
      r('POST', `${org}/members/import/preview`, () => ({
        error: null,
        rows: [
          { rowNumber: 2, fullName: 'Karin Ek', email: 'karin@example.se', phone: '070-1112233', propertyNumber: 'Berga 2:4', sharePercentage: 5, role: 'member', error: null },
          { rowNumber: 3, fullName: 'Olof Lind', email: 'olof@example.se', phone: '', propertyNumber: 'Berga 2:5', sharePercentage: 5, role: 'member', error: null },
          { rowNumber: 4, fullName: 'Felaktig rad', email: 'saknar-snabel-a', phone: '', propertyNumber: '', sharePercentage: 0, role: 'member', error: 'Ogiltig e-postadress.' },
        ],
      })),
      r('POST', `${org}/members/import`, (_, b) => {
        const added: Member[] = b.members.map((m: Member) => ({ ...m, id: newId('m'), userId: newId('u'), createdAt: now(), isPending: true }));
        s.members.push(...added);
        notify(NO_MESSAGES_NOTICE);
        return { created: added.length, alreadyMembers: 0, notImportedDueToQuota: 0 };
      }),
      r('PUT', `${org}/members/${id}`, ([memberId], b) => Object.assign(find(s.members, memberId), b)),
      r('PATCH', `${org}/members/${id}/email`, ([memberId], b) => Object.assign(find(s.members, memberId), { email: b.email })),
      r('DELETE', `${org}/members/${id}`, ([memberId]) => { s.members = s.members.filter(m => m.id !== memberId); }),

      r('GET', `${org}/posts`, () => [...s.posts].sort((a, b) => b.createdAt.localeCompare(a.createdAt))),
      r('POST', `${org}/posts`, (_, b) => {
        const post: Post = {
          id: newId('p'), title: b.title, content: b.content, postType: 'post', imageUrl: null, createdAt: now(),
          updatedAt: now(), author: this.author(), attachments: [], comments: [], meeting: null,
        };
        s.posts.push(post);
        return post;
      }),
      r('PUT', `${org}/posts/${id}`, ([postId], b) => Object.assign(find(s.posts, postId), b, { updatedAt: now() })),
      r('DELETE', `${org}/posts/${id}`, ([postId]) => { s.posts = s.posts.filter(p => p.id !== postId); }),
      r('POST', `${org}/posts/${id}/image`, ([postId], b) => {
        const imageUrl = uploadedFile(b).url;
        find(s.posts, postId).imageUrl = imageUrl;
        return { imageUrl };
      }),
      r('POST', `${org}/posts/${id}/attachments`, ([postId], b) => {
        const a = attachment(b);
        find(s.posts, postId).attachments.push(a);
        return a;
      }),
      r('DELETE', `${org}/posts/${id}/attachments/${id}`, ([postId, attachmentId]) => {
        const post = find(s.posts, postId);
        post.attachments = post.attachments.filter(a => a.id !== attachmentId);
      }),
      r('POST', `${org}/posts/${id}/comments`, ([postId], b) => {
        const comment = { id: newId('c'), content: b.content, createdAt: now(), author: this.author() };
        find(s.posts, postId).comments.push(comment);
        return comment;
      }),
      r('DELETE', `${org}/posts/${id}/comments/${id}`, ([postId, commentId]) => {
        const post = find(s.posts, postId);
        post.comments = post.comments.filter(c => c.id !== commentId);
      }),

      r('GET', `${org}/meetings`, () => s.meetings),
      r('POST', `${org}/meetings`, (_, b) => {
        const meeting: Meeting = {
          id: newId('mt'), title: b.title, description: b.description, location: b.location, meetingDate: b.meetingDate,
          createdAt: now(), notificationChannel: b.notificationChannel, attachments: [], rsvps: [], myRsvp: null, protocol: null,
        };
        s.meetings.push(meeting);
        s.posts.push({
          id: newId('p'), title: meeting.title, content: meeting.description, postType: 'meeting', imageUrl: null,
          createdAt: now(), updatedAt: now(), author: this.author(), attachments: [], comments: [],
          meeting: { id: meeting.id, title: meeting.title, description: meeting.description, meetingDate: meeting.meetingDate, location: meeting.location },
        });
        notify(NO_MESSAGES_NOTICE);
        return meeting;
      }),
      r('PUT', `${org}/meetings/${id}`, ([meetingId], b) => {
        const meeting = Object.assign(find(s.meetings, meetingId), b);
        syncMeetingPost(meeting);
        return meeting;
      }),
      r('DELETE', `${org}/meetings/${id}`, ([meetingId]) => {
        s.meetings = s.meetings.filter(m => m.id !== meetingId);
        s.posts = s.posts.filter(p => p.meeting?.id !== meetingId);
      }),
      r('POST', `${org}/meetings/${id}/rsvp`, ([meetingId], b) => {
        const meeting = find(s.meetings, meetingId);
        meeting.myRsvp = b.status || null;
        meeting.rsvps = meeting.rsvps.filter(rsvp => rsvp.userId !== DEMO_USER_ID);
        if (b.status) meeting.rsvps.push({ id: newId('r'), userId: DEMO_USER_ID, fullName: s.profile.fullName, status: b.status });
      }),
      r('POST', `${org}/meetings/${id}/attachments`, ([meetingId], b) => {
        const a = attachment(b);
        find(s.meetings, meetingId).attachments.push(a);
        return a;
      }),
      r('DELETE', `${org}/meetings/${id}/attachments/${id}`, ([meetingId, attachmentId]) => {
        const meeting = find(s.meetings, meetingId);
        meeting.attachments = meeting.attachments.filter(a => a.id !== attachmentId);
      }),
      r('POST', `${org}/meetings/${id}/protocol`, ([meetingId], b) => {
        const meeting = find(s.meetings, meetingId);
        const existing = meeting.protocol;
        if (existing && (existing.title !== b.title || existing.content !== b.content)) {
          existing.status = 'draft';
          existing.approvals.forEach(a => { a.status = 'pending'; });
        }
        meeting.protocol = existing
          ? Object.assign(existing, b)
          : { id: newId('pr'), title: b.title, content: b.content, status: 'draft', createdAt: now(), approvals: [] };
        return meeting.protocol;
      }),
      r('POST', `${org}/meetings/${id}/protocol/approve`, ([meetingId], b) => {
        const meeting = find(s.meetings, meetingId);
        const protocol = meeting.protocol;
        const approval = protocol?.approvals.find(a => a.userId === DEMO_USER_ID);
        if (approval) Object.assign(approval, { status: b.status, comment: b.comment ?? null });
        if (protocol && protocol.status !== 'approved' && protocol.approvals.every(a => a.status === 'approved')) {
          protocol.status = 'approved';
          const folder = s.folders.find(f => f.name === 'Protokoll' && f.parentFolderId === null);
          if (folder) {
            s.documents.push({
              id: newId('d'), folderId: folder.id, name: `protokoll-${meeting.meetingDate.slice(0, 10)}-${protocol.title}.pdf`,
              fileSize: 0, uploadedBy: s.profile.fullName, downloadUrl: '', createdAt: now(),
            });
          }
          notify(NO_MESSAGES_NOTICE);
        }
      }),
      r('POST', `${org}/meetings/${id}/protocol/approvers`, ([meetingId], b) => {
        const protocol = find(s.meetings, meetingId).protocol;
        const member = s.members.find(m => m.userId === b.userId);
        const approval = {
          id: newId('ap'), userId: b.userId, fullName: member?.fullName ?? '', status: 'pending', comment: null, createdAt: now(),
        };
        protocol?.approvals.push(approval);
        notify(NO_MESSAGES_NOTICE);
        return approval;
      }),

      r('GET', `${org}/payments`, () => s.paymentPeriods),
      r('POST', `${org}/payments`, (_, b) => {
        const period: PaymentPeriod = {
          id: newId('pp'), title: b.title, description: b.description, amount: b.amount, dueDate: b.dueDate,
          createdAt: now(), financialYearId: b.financialYearId ?? null, memberPayments: this.memberPayments(b),
        };
        s.paymentPeriods.push(period);
        return period;
      }),
      r('PUT', `${org}/payments/${id}`, ([periodId], b) => {
        const period = Object.assign(find(s.paymentPeriods, periodId), b);
        period.memberPayments = this.memberPayments(period, period.memberPayments);
        return period;
      }),
      r('DELETE', `${org}/payments/${id}`, ([periodId]) => {
        s.paymentPeriods = s.paymentPeriods.filter(p => p.id !== periodId);
      }),
      r('PUT', `${org}/payments/${id}/members/${id}`, ([periodId, paymentId], b) => {
        Object.assign(find(find(s.paymentPeriods, periodId).memberPayments, paymentId), { isPaid: b.isPaid, paidAt: b.isPaid ? now() : null });
      }),
      r('POST', `${org}/payments/${id}/regenerate-invoices`, ([periodId]) => {
        const period = find(s.paymentPeriods, periodId);
        period.memberPayments.forEach((p, i) => {
          p.invoiceNumber = p.invoiceNumber ?? `${new Date().getFullYear()}-${String(i + 1).padStart(3, '0')}`;
          p.invoiceDownloadUrl = `/organizations/demo/payments/${periodId}/members/${p.id}/invoice`;
        });
        return period;
      }),
      r('POST', `${org}/payments/${id}/members/${id}/notify`, () => {
        notify(NO_MESSAGES_NOTICE);
        return { smsSent: false, emailSent: false };
      }),

      r('GET', `${org}/financial-years`, () => s.financialYears.map(y => this.financialYearSummary(y))),
      r('GET', `${org}/financial-years/${id}`, ([yearId]): FinancialYearDetail => {
        const y = year(yearId);
        return {
          summary: this.financialYearSummary(y),
          paymentPeriods: s.paymentPeriods.filter(p => p.financialYearId === y.id),
          supplierInvoices: y.supplierInvoices,
          otherCosts: y.otherCosts,
        };
      }),
      r('POST', `${org}/financial-years`, (_, b) => {
        const y: DemoFinancialYear = {
          id: newId('fy'), year: b.year, startDate: b.startDate, endDate: b.endDate, status: 'open',
          openingBalance: b.openingBalance, totalIncome: 0, totalExpenses: 0, totalReceivables: 0, balance: 0,
          closedAt: null, reportDownloadUrl: null, createdAt: now(), supplierInvoices: [], otherCosts: [],
        };
        s.financialYears.push(y);
        return this.financialYearSummary(y);
      }),
      r('PUT', `${org}/financial-years/${id}`, ([yearId], b) => this.financialYearSummary(Object.assign(year(yearId), b))),
      r('POST', `${org}/financial-years/${id}/close`, ([yearId]) =>
        this.financialYearSummary(Object.assign(year(yearId), { status: 'closed', closedAt: now() }))),
      r('POST', `${org}/financial-years/${id}/reopen`, ([yearId]) =>
        this.financialYearSummary(Object.assign(year(yearId), { status: 'open', closedAt: null }))),
      r('POST', `${org}/financial-years/${id}/generate-report`, ([yearId]) =>
        this.financialYearSummary(Object.assign(year(yearId), { reportDownloadUrl: `/organizations/demo/financial-years/${yearId}/report` }))),
      r('DELETE', `${org}/financial-years/${id}`, ([yearId]) => {
        s.financialYears = s.financialYears.filter(y => y.id !== yearId);
      }),
      r('POST', `${org}/financial-years/${id}/supplier-invoices`, ([yearId], b) => {
        const invoice = { ...b, id: newId('si'), isPaid: false, paidAt: null, fileDownloadUrl: null, createdAt: now() };
        year(yearId).supplierInvoices.push(invoice);
        return invoice;
      }),
      r('POST', `${org}/financial-years/${id}/supplier-invoices/${id}/file`, ([yearId, invoiceId], b) =>
        Object.assign(find(year(yearId).supplierInvoices, invoiceId), { fileDownloadUrl: uploadedFile(b).url })),
      r('PUT', `${org}/financial-years/${id}/supplier-invoices/${id}`, ([yearId, invoiceId], b) => {
        const invoice = find(year(yearId).supplierInvoices, invoiceId);
        const paidAt = b.isPaid === invoice.isPaid ? invoice.paidAt : b.isPaid ? now() : null;
        return Object.assign(invoice, b, { paidAt });
      }),
      r('DELETE', `${org}/financial-years/${id}/supplier-invoices/${id}`, ([yearId, invoiceId]) => {
        const y = year(yearId);
        y.supplierInvoices = y.supplierInvoices.filter(i => i.id !== invoiceId);
      }),
      r('POST', `${org}/financial-years/${id}/other-costs`, ([yearId], b) => {
        const cost = { ...b, id: newId('oc'), fileDownloadUrl: null, createdAt: now() };
        year(yearId).otherCosts.push(cost);
        return cost;
      }),
      r('POST', `${org}/financial-years/${id}/other-costs/${id}/file`, ([yearId, costId], b) =>
        Object.assign(find(year(yearId).otherCosts, costId), { fileDownloadUrl: uploadedFile(b).url })),
      r('PUT', `${org}/financial-years/${id}/other-costs/${id}`, ([yearId, costId], b) =>
        Object.assign(find(year(yearId).otherCosts, costId), b)),
      r('DELETE', `${org}/financial-years/${id}/other-costs/${id}`, ([yearId, costId]) => {
        const y = year(yearId);
        y.otherCosts = y.otherCosts.filter(c => c.id !== costId);
      }),

      r('GET', `${org}/folders`, () => s.folders.map(f => ({ ...f, documentCount: s.documents.filter(d => d.folderId === f.id).length }))),
      r('POST', `${org}/folders`, (_, b) => {
        const folder = { id: newId('f'), name: b.name, parentFolderId: b.parentFolderId ?? null, documentCount: 0, createdAt: now() };
        s.folders.push(folder);
        return folder;
      }),
      r('DELETE', `${org}/folders/${id}`, ([folderId]) => {
        s.folders = s.folders.filter(f => f.id !== folderId);
        s.documents = s.documents.filter(d => d.folderId !== folderId);
      }),
      r('GET', `${org}/folders/${id}/documents`, ([folderId]) => s.documents.filter(d => d.folderId === folderId)),
      r('POST', `${org}/folders/${id}/documents`, ([folderId], b) => {
        const file = uploadedFile(b);
        const document = {
          id: newId('d'), folderId, name: file.name, fileSize: file.size, uploadedBy: s.profile.fullName,
          downloadUrl: file.url, createdAt: now(),
        };
        s.documents.push(document);
        return document;
      }),
      r('DELETE', `${org}/documents/${id}`, ([documentId]) => {
        s.documents = s.documents.filter(d => d.id !== documentId);
      }),

      r('GET', `${org}/proposals`, () => s.proposals),
      r('POST', `${org}/proposals`, (_, b) => {
        const meeting = s.meetings.find(m => m.id === b.linkedMeetingId);
        const proposal = {
          id: newId('pp'), title: b.title, content: b.content, status: 'pending',
          linkedMeetingId: meeting?.id ?? null, linkedMeetingTitle: meeting?.title ?? null, createdAt: now(),
          submittedBy: { id: DEMO_USER_ID, fullName: this.me().fullName },
        };
        s.proposals.push(proposal);
        return proposal;
      }),
      r('PUT', `${org}/proposals/${id}/status`, ([proposalId], b) => { find(s.proposals, proposalId).status = b.status; }),
      r('DELETE', `${org}/proposals/${id}`, ([proposalId]) => {
        s.proposals = s.proposals.filter(p => p.id !== proposalId);
      }),

      r('GET', `${org}/notifications`, (): Notifications => {
        const seen = { lastSeenAt: now(), unreadCount: 0 };
        return { feed: seen, payments: seen, documents: seen, proposals: seen, members: seen };
      }),
      r('PUT', `${org}/notifications/${id}`, () => undefined),

      r('GET', `${org}/subscription`, () => ({ status: 'active', trialEndsAt: null, paidUntil: null, publishableKey: '', tier: s.org.subscriptionTier, cancelAtPeriodEnd: false, checkoutEnabled: false })),
      r('POST', `${org}/subscription/checkout`, notAvailable),
      r('POST', `${org}/subscription/portal`, notAvailable),
    ];
  }
}

// Pages mutate what they receive (optimistic updates), so hand out copies to keep the store authoritative.
function copy<T>(value: T): T {
  return value === undefined ? value : JSON.parse(JSON.stringify(value));
}
